import axios from "axios";
import * as turf from "@turf/turf";
import GtfsRealtimeBindings from "gtfs-realtime-bindings";

import {
  ACTIVE_STOP,
  DISPATCH_MODAL_INFO_BOTTOM,
  DISPATCH_MODAL_SETTINGS,
  DISPATCH_OCCUPANCY_STATUS,
  DISPATCH_ZOOM,
  FETCH_DIRECTIONS,
  FETCH_ID,
  GET_ACTIVE_BLOCKS,
  GET_ACTIVE_BUSES,
  GET_ALL_BUSES,
  GET_ALL_POLYLINES_STOPS,
  GET_LINES_LIST,
  GET_LOCALE,
  GET_OCCUPANCY_STATUS,
  GET_POLYLINES_STOPS,
  SELECT_ROUTE,
  SELECT_STOP,
  SET_ALL_ROUTES_STOPS,
  SET_GRAYSCALE_MAP,
  SET_LOADING,
  SHOW_LIVE_BUSES,
  SHOW_SCHEDULED_BUSES,
} from "./actionTypes";
import {
  apiKey,
  siriApiKey,
  listPositionForAgency,
  apiBaseURL,
  themeProviderCoMB,
  axiosConfig,
  urlGTFS_RT_OccupancyData,
} from "./../config";
import { store } from "../redux/store";
import {
  cutAgencyName,
  dynamicTexColor,
  getAcronym,
  getOccupancyStatusString,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  logCons,
  removeDuplicateStops,
  sortNumericallyAndAlphabetically,
} from "../utils/helpers";

export const fetchAgencyID = () => async (dispatch: Dispatch) => {
  await axios
    .get(`${apiBaseURL}/api/where/agencies-with-coverage.json?key=${apiKey}`, axiosConfig)
    .then(({ data }) => {
      // console.log("data:", data);
      const statusCode = data.code;
      const currentTime = data.currentTime;

      if (listPositionForAgency || listPositionForAgency === 0) {
        const agencyId = data.data.list[listPositionForAgency].agencyId;
        const lat = data.data.list[listPositionForAgency].lat;
        const latSpan = data.data.list[listPositionForAgency].latSpan;
        const lon = data.data.list[listPositionForAgency].lon;
        const lonSpan = data.data.list[listPositionForAgency].lonSpan;
        const initialMapRange = { lat, latSpan, lon, lonSpan };
        const timeZone = data.data.references.agencies[listPositionForAgency].timezone;
        // console.log({ currentTime, agencyId, initialMapRange, timeZone });
        dispatch({ type: FETCH_ID, payload: { statusCode, agencyId, currentTime, initialMapRange, timeZone } });
      } else {
        //* Area for a few agencies
        const agencyArray: { agencyId: string; agencyName: string }[] = [];
        const agencyId = data.data.list[0].agencyId;
        for (let i = 0; i < data.data.references.agencies.length; i++) {
          const agencyObject = {
            agencyId: data.data.references.agencies[i].id,
            agencyName: data.data.references.agencies[i].name,
          };
          agencyArray.push(agencyObject);
        }
        const areaArray = [];
        for (let i = 0; i < data.data.list.length; i++) {
          const singleAreaArray = [
            [data.data.list[i].lat - data.data.list[i].latSpan / 2, data.data.list[i].lon - data.data.list[i].lonSpan / 2],
            [data.data.list[i].lat + data.data.list[i].latSpan / 2, data.data.list[i].lon + data.data.list[i].lonSpan / 2],
          ];
          areaArray.push(singleAreaArray);
        }
        const flatAreaArray = areaArray.flat(1);
        // console.log("flatAreaArray:", flatAreaArray);

        const multiPoint = turf.multiPoint(flatAreaArray);
        const bBox = turf.bbox(multiPoint);
        const bboxPolygon = turf.bboxPolygon(bBox);
        const areaPolygon = bboxPolygon?.bbox;
        // console.log("areaPolygon:", areaPolygon);
        const lat = (areaPolygon![0] + areaPolygon![2]) / 2;
        const lon = (areaPolygon![1] + areaPolygon![3]) / 2;
        const latSpan = Math.abs(areaPolygon![2] - areaPolygon![0]);
        const lonSpan = Math.abs(areaPolygon![3] - areaPolygon![1]);
        const initialMapRange = { lat, latSpan, lon, lonSpan };
        // console.log({ code, currentTime, agencyId,initialMapRange  });
        dispatch({ type: FETCH_ID, payload: { statusCode, agencyId, currentTime, initialMapRange, agencyArray } });
      }
    })
    .catch((error) => {
      if (error.response) {
        console.error("error.response.status:", error.response.status);
      }
      console.error("error.config:", error.config);
    });
};

export const fetchLinesList = (agencyId: string) => async (dispatch: Dispatch) => {
  await axios
    .get(`${apiBaseURL}/api/where/route-ids-for-agency/${agencyId}.json?key=${apiKey}`, axiosConfig)
    .then(({ data }) => {
      // console.log("data:", data.data);
      let unsortedList = data?.data?.list;
      // console.log({ unsortedList });
      // console.log({ agencyId });
      unsortedList = unsortedList.map((item: string) => cutAgencyName(item, agencyId));
      // console.log({ list });

      const list: string[] = sortNumericallyAndAlphabetically(unsortedList);
      // console.log("list:", list);
      dispatch({ type: GET_LINES_LIST, payload: { list } });
    })
    .catch((error) => {
      console.log("error:", error);
    });
};

export const selectRoute = (routeNumber: number | string) => async (dispatch: Dispatch) => {
  // console.log({ routeNumber });
  try {
    dispatch({ type: SELECT_ROUTE, payload: { routeNumber } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const fetchPolylines_Stops = (agencyId: string, routeNumber: string) => async (dispatch: Dispatch) => {
  // console.log({ agencyId, routeNumber });
  await axios
    .get(
      `${apiBaseURL}/api/where/stops-for-route/${agencyId}_${routeNumber}.json?includePolylines=true&key=${apiKey}`,
      axiosConfig
    )
    .then(({ data }) => {
      // console.log({ routeNumber });
      dispatch({ type: GET_POLYLINES_STOPS, payload: data.data });
    })
    .catch((error) => {
      if (error.response) {
        console.error("error.response.status:", error.response.status);
      }
      console.error("error.config:", error.config);
    });
};

export const getActiveBuses = (agencyId: string, routeNumber: number | string) => async (dispatch: Dispatch) => {
  // console.log({ agencyId, routeNumber });
  await axios
    .get(
      `${apiBaseURL}/siri/vehicle-monitoring?key=${siriApiKey}&OperatorRef=${agencyId}&LineRef=${routeNumber}&type=json&ShowRawLocation=true`,
      axiosConfig
    )
    .then(({ data }) => {
      // console.log("data:", data);
      const dataToFetch = data?.Siri?.ServiceDelivery?.VehicleMonitoringDelivery["0"].VehicleActivity;
      // console.log("dataToFetch:", dataToFetch);
      dispatch({ type: GET_ACTIVE_BUSES, payload: { buses: dataToFetch } });
    })
    .catch((error) => {
      console.log("error:", error);
    });
};

export const getStopInfo = (stopId: string, time: string) => async (dispatch: Dispatch) => {
  // console.log({ stopId, time });
  const now = Date.now();
  // console.log({ now });
  await axios
    .get(
      `${apiBaseURL}/api/where/arrivals-and-departures-for-stop/${stopId}.json?minutesAfter=${time}&key=${apiKey}`,
      axiosConfig
    )
    .then(async ({ data }) => {
      const dataToFetch = await data?.data?.entry?.arrivalsAndDepartures;
      const dataToSet = await dataToFetch?.sort(
        (a: { scheduledDepartureTime: number }, b: { scheduledDepartureTime: number }) =>
          a.scheduledDepartureTime - b.scheduledDepartureTime
      );
      const dataToSetFiltered = await dataToSet?.filter(
        (set: { scheduledDepartureTime: number }) => set?.scheduledDepartureTime < now + 24 * 60 * 60 * 1000
      );
      // console.log("dataToSetFiltered:", dataToSetFiltered);
      dispatch({ type: ACTIVE_STOP, payload: { arrivalsDepartures: dataToSetFiltered } });
    })
    .catch((error) => {
      console.log("error:", error);
    });
};

export const getActiveBlocks = () => async (dispatch: Dispatch) => {
  const agencyId = await store.getState()?.agency?.agencyId;
  const { lat, latSpan, lon, lonSpan } = store.getState()?.agency?.initialMapRange;

  await axios
    .get(
      `${apiBaseURL}/api/where/trips-for-location.json?lat=${lat}&lon=${lon}&latSpan=${latSpan}&lonSpan=${lonSpan}&key=${apiKey}`,
      axiosConfig
    )
    .then(({ data }) => {
      // console.log("data:", data);
      const dataToFetch = data?.data?.references?.routes;
      // console.log("dataToFetch:", dataToFetch);
      const dataToSet = dataToFetch.map((elem: { id: string }) => elem?.id);
      // console.log("dataToSet:", dataToSet);
      const activeBlocks = dataToSet.map((elem: string) => cutAgencyName(elem, agencyId));
      // console.log("activeBlocks:", activeBlocks);
      dispatch({ type: GET_ACTIVE_BLOCKS, payload: { activeBlocks: activeBlocks } });
    })
    .catch((error) => {
      // console.log("error:", error);
      if (axios.isAxiosError(error)) {
        console.log("error?.response?.status:", error?.response?.status);
      }
    });
};

export const setLoadingAction = (setLoading: boolean) => async (dispatch: Dispatch) => {
  // console.log({ setLoading });
  try {
    dispatch({ type: SET_LOADING, payload: { setLoading } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const getLocale = (selectedLanguage: string) => async (dispatch: Dispatch) => {
  // console.log({ selectedLanguage });
  try {
    dispatch({ type: GET_LOCALE, payload: { selectedLanguage } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const dispatchZoom = (zoom: number) => async (dispatch: Dispatch) => {
  // console.log({ zoom });
  try {
    dispatch({ type: DISPATCH_ZOOM, payload: { zoom } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const getDirections = (agencyId: string, routeNumber: number | string) => async (dispatch: Dispatch) => {
  // console.log({ routeNumber });
  await axios
    .get(`${apiBaseURL}/api/where/stops-for-route/${agencyId}_${routeNumber}.json?key=${apiKey}&type=JSON`, axiosConfig)
    .then(({ data }) => {
      const stopsWithInfo = data?.data?.references?.stops;
      const dataToDispatch = data?.data?.entry?.stopGroupings[0]?.stopGroups;
      let directionsArray: { id: number; name: string; stops: string[] }[] = [];
      dataToDispatch.forEach((direction: { stopIds: string[]; id: string; name: { name: string } }) => {
        directionsArray.push({
          id: Number(direction.id),
          name: direction.name.name,
          stops: direction.stopIds,
        });
      });
      directionsArray = directionsArray.sort((a, b) => a.id - b.id);
      // console.log("directionsArray", directionsArray);
      dispatch({
        type: FETCH_DIRECTIONS,
        payload: {
          directionsArray: directionsArray,
          directionsNumber: directionsArray.length,
          stopsWithInfo: stopsWithInfo,
        },
      });
    })
    .catch((error) => {
      console.log("error:", error);
    });
};

export const selectStop = (stop: StopInfo, zoomTo: boolean) => async (dispatch: Dispatch) => {
  // console.log({ stop });
  try {
    dispatch({ type: SELECT_STOP, payload: { zoomed: zoomTo, stop: stop } });
  } catch (error) {
    console.log("error:", error);
  }
};

//* New Start Site - Get all polylines, stops and buses
export const getAllPolylinesStops =
  () =>
  async (dispatch: Dispatch, getState: Fetch): Promise<void> => {
    const list = getState()?.list;
    const agencyId = getState()?.agency?.agencyId;
    // console.log("list:", list);
    // console.log("agencyId:", agencyId);

    const polylines: AllPolylinesStops[] = await getState()?.allData?.polylinesStops;
    // console.log("polylines:", polylines);
    if (polylines?.length) {
      return;
    }

    if (list && list?.list && agencyId) {
      // console.log("list:", list);
      const endpoints: string[] = list?.list?.map(
        (routeNumber: number) =>
          `${apiBaseURL}/api/where/stops-for-route/${agencyId}_${routeNumber}.json?includePolylines=true&key=${apiKey}`,
        axiosConfig
      );
      // console.log("endpoints:", endpoints);
      const promises = endpoints.map((endpoint: string) => axios.get(endpoint, axiosConfig));
      // console.log("promises:", promises);

      await Promise.allSettled(promises)
        .then((responses) => {
          // console.log("responses:", responses);
          const polylinesStops = [] as AllPolylinesStops[];
          const allRoutesTable = [] as RouteInfo[];
          const allStopsTable = [] as StopInfo[];

          const fulfilledResponse = responses.filter((response) => response.status === "fulfilled");
          fulfilledResponse.forEach((element) => {
            // console.log({ element });
            if (element?.status === "fulfilled") {
              polylinesStops.push({
                routeName_0:
                  element?.value?.data?.data?.entry?.stopGroupings[0]?.stopGroups?.filter(
                    (elem: { id: string }) => elem.id === "0"
                  )[0]?.name?.name || "",
                routeName_1:
                  element?.value?.data?.data?.entry?.stopGroupings[0]?.stopGroups?.filter(
                    (elem: { id: string }) => elem.id === "1"
                  )[0]?.name?.name || "",
                routeId: element?.value?.data?.data?.entry?.routeId,
                polylines: element?.value?.data?.data?.entry?.polylines,
                stops: element?.value?.data?.data?.references?.stops,
                addedBusIcon: "",
              });
              allRoutesTable.push(element?.value?.data?.data?.references?.routes);
              allStopsTable.push(element?.value?.data?.data?.references?.stops);
            }
          });
          const allRoutesTableFlat = allRoutesTable.flat(1);
          const allRoutesTableReduced = removeDuplicateStops(allRoutesTableFlat, "id");

          //* Adding white textColor if doesn't exist (e.g. for Doral)
          for (let i = 0; i < allRoutesTableReduced.length; i++) {
            if (allRoutesTableReduced[i].textColor === "" || !allRoutesTableReduced[i].textColor) {
              allRoutesTableReduced[i].textColor = "ffffff";
            }
          }
          //* Adding black color if doesn't exist (e.g. for Kombus 598)
          for (let i = 0; i < allRoutesTableReduced.length; i++) {
            if (allRoutesTableReduced[i].color === "" || !allRoutesTableReduced[i].color) {
              allRoutesTableReduced[i].color = "000000";
            }
          }

          for (let i = 0; i < allRoutesTableReduced.length; i++) {
            allRoutesTableReduced[i].textColor = dynamicTexColor(allRoutesTableReduced[i].color);
          }

          //* Add acronym do Miami Beach
          if (themeProviderCoMB && agencyId === ("2362" as string)) {
            for (let i = 0; i < allRoutesTableReduced.length; i++) {
              if (!allRoutesTableReduced[i].shortName) {
                allRoutesTableReduced[i].shortName = getAcronym(allRoutesTableReduced[i].longName);
              }
            }
          }

          const allStopsTableFlat = allStopsTable.flat(1) as StopInfo[];
          const allStopsTableReduced = removeDuplicateStops(allStopsTableFlat, "id") as StopInfo[];

          //* Adding custom icon for CoMB
          if (themeProviderCoMB) {
            for (let i = 0; i < polylinesStops.length; i++) {
              polylinesStops[i].addedBusIcon = `icon${cutAgencyName(polylinesStops[i].routeId, agencyId)}`;
            }
          }
          // console.log("polylinesStops:", polylinesStops, polylinesStops.length);

          dispatch({
            type: GET_ALL_POLYLINES_STOPS,
            payload: { polylinesStops, allRoutesTableReduced, allStopsTableReduced },
          });
        })
        .catch((error) => {
          console.log("error:", error);
        });
    }
  };

export const getAllBuses = () => async (dispatch: Dispatch) => {
  const activeBlocks = store.getState()?.activeBlocks;
  const agencyId = store.getState()?.agency?.agencyId;
  // console.log("activeBlocks:", activeBlocks);

  if (activeBlocks && activeBlocks?.activeBlocks) {
    // console.log("activeBlocks:", activeBlocks);
    const endpoints: string[] = activeBlocks?.activeBlocks?.map(
      (routeNumber: number) =>
        `${apiBaseURL}/siri/vehicle-monitoring?key=${siriApiKey}&OperatorRef=${agencyId}&LineRef=${routeNumber}&type=json&ShowRawLocation=true`,
      axiosConfig
    );
    // console.log("endpoints:", endpoints);
    const promises = endpoints.map((endpoint: string) => axios.get(endpoint, axiosConfig));
    // console.log("promises:", promises);

    await Promise.allSettled(promises)
      .then((responses) => {
        // console.log("responses:", responses);
        let activeBlocksBuses = [] as BusInfo[];

        const fulfilledResponse = responses.filter((response) => response.status === "fulfilled");
        fulfilledResponse.forEach((element) => {
          if (element?.status === "fulfilled") {
            activeBlocksBuses.push(
              element?.value?.data?.Siri?.ServiceDelivery?.VehicleMonitoringDelivery["0"].VehicleActivity
            );
          }
        });
        activeBlocksBuses = activeBlocksBuses.flat(1);
        activeBlocksBuses = activeBlocksBuses.filter((activeBlock) => activeBlock !== undefined);
        // console.log("activeBlocksBuses:", activeBlocksBuses, activeBlocksBuses.length);
        dispatch({ type: GET_ALL_BUSES, payload: { activeBlocksBuses } });
      })
      .catch((error) => {
        console.log("error:", error);
      });
  }
};

//* New Mobile Layout
export const dispatchModalSettingsIsOpen = (isModalSettingsOpen: boolean) => async (dispatch: Dispatch) => {
  // console.log({ isModalSettingsOpen });
  try {
    dispatch({ type: DISPATCH_MODAL_SETTINGS, payload: { isModalSettingsOpen } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const dispatchModalInfoBottomIsOpen = (isModalInfoBottomOpen: boolean) => async (dispatch: Dispatch) => {
  try {
    dispatch({ type: DISPATCH_MODAL_INFO_BOTTOM, payload: { isModalInfoBottomOpen } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const dispatchShowLiveBuses = (showLiveBuses: boolean) => async (dispatch: Dispatch) => {
  try {
    dispatch({ type: SHOW_LIVE_BUSES, payload: { showLiveBuses } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const dispatchShowScheduledBuses = (showScheduledBuses: boolean) => async (dispatch: Dispatch) => {
  try {
    dispatch({ type: SHOW_SCHEDULED_BUSES, payload: { showScheduledBuses } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const setGrayscaleMap = (grayscaleMap: boolean) => async (dispatch: Dispatch) => {
  try {
    dispatch({ type: SET_GRAYSCALE_MAP, payload: { grayscaleMap } });
  } catch (error) {
    console.log("error:", error);
  }
};

export const dispatchShowAllRoutesStops = (setAllRoutesStops: boolean) => async (dispatch: Dispatch) => {
  try {
    dispatch({ type: SET_ALL_ROUTES_STOPS, payload: { setAllRoutesStops } });
  } catch (error) {
    console.log("error:", error);
  }
};

//* New features
export const getOccupancyStatusData =
  () =>
  async (dispatch: Dispatch, getState: Fetch): Promise<void> => {
    const occupancyStatus: boolean = getState()?.appSettings?.occupancyStatus;

    if (!urlGTFS_RT_OccupancyData || !occupancyStatus) {
      return;
    }

    const vehiclePositionsData = [] as VehiclePositionsData[];

    try {
      const response = await axios.get(urlGTFS_RT_OccupancyData, { headers: {}, responseType: "arraybuffer" });
      if (response.status !== 200) {
        const error = new Error(`${response.config.url}: ${response.status} ${response.statusText}`);
        console.log("error:", error);
      }

      const buffer: Uint8Array = new Uint8Array(response.data);
      const feed = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(
        buffer
      ) as GtfsRealtimeBindings.transit_realtime.FeedMessage;
      // console.log("feed?.entity:", feed?.entity);

      feed?.entity.forEach((dataGTFS: GtfsRealtimeBindings.transit_realtime.IFeedEntity) => {
        const objectDataGTFS = {
          vehicleName: dataGTFS?.id as string,
          vehicleId: dataGTFS?.vehicle?.vehicle?.id as string,
          routeId: dataGTFS?.vehicle?.trip?.routeId as string,
          tripId: dataGTFS?.vehicle?.trip?.tripId as string,
          occupancyStatus: (dataGTFS?.vehicle as GtfsRealtimeBindings.transit_realtime.IVehiclePosition)?.hasOwnProperty(
            "occupancyStatus"
          )
            ? (getOccupancyStatusString(dataGTFS?.vehicle?.occupancyStatus as number) as string)
            : null, //* Need to check hasOwnProperty!
        };
        vehiclePositionsData.push(objectDataGTFS);
      });

      // logCons({ vehiclePositionsData });
      dispatch({ type: GET_OCCUPANCY_STATUS, payload: { vehiclePositionsData } });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log("error?.response?.status:", error?.response?.status);
      }
      if (error instanceof Error) {
        console.log("error?.message:", error?.message);
        console.log("error?.status:", error?.name);
      }
    }
  };

export const dispatchOccupancyStatus = (occupancyStatus: boolean) => async (dispatch: Dispatch) => {
  try {
    dispatch({ type: DISPATCH_OCCUPANCY_STATUS, payload: { occupancyStatus } });
  } catch (error) {
    console.log("error:", error);
  }
};
